/**
 * Short share URL system for supplier stores.
 * Creates a short code via og-meta (POST) and returns a branded URL:
 * https://<host>/<code>
 */

const SUPABASE_PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID || 'qfozcnxbtjjmidhjdepc';
const OG_FUNCTION_URL = `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/og-meta`;
const DEFAULT_SHARE_HOST = 'amecardapio.com';

const normalizeHost = (host?: string | null): string | null => {
  if (!host) return null;
  const normalized = host.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  return normalized || null;
};

const buildHostUrl = (host: string, path: string) => `https://${host}${path}`;

/**
 * Creates a short shareable URL for the store.
 * It prioritizes host selection in this order:
 * 1) preferredHost (client-provided)
 * 2) share_host returned by edge function (supplier custom domain)
 * 3) DEFAULT_SHARE_HOST
 */
const SHARE_CACHE_KEY = 'ameup_share_link_cache_v1';
const SHARE_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const inFlightShareLinks = new Map<string, Promise<string>>();

const shareCacheKey = (supplierSlug: string, queryParams?: Record<string, string>, preferredHost?: string | null) => {
  const sorted = Object.entries(queryParams || {})
    .filter(([, v]) => !!v)
    .sort(([a], [b]) => a.localeCompare(b));
  return `${supplierSlug}|${normalizeHost(preferredHost) || ''}|${JSON.stringify(sorted)}`;
};

const readShareCache = (): Record<string, { url: string; t: number }> => {
  try {
    const raw = localStorage.getItem(SHARE_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeShareCache = (key: string, url: string) => {
  try {
    const cache = readShareCache();
    const now = Date.now();
    Object.keys(cache).forEach((k) => { if (now - cache[k].t > SHARE_CACHE_TTL_MS) delete cache[k]; });
    cache[key] = { url, t: now };
    localStorage.setItem(SHARE_CACHE_KEY, JSON.stringify(cache));
  } catch { /* sem localStorage: segue sem cache */ }
};

/**
 * Cria (ou reaproveita) o link curto da loja. Os mesmos parâmetros no mesmo navegador reutilizam o link já
 * criado (e chamadas simultâneas idênticas dividem a mesma requisição), em vez de gravar um link novo a cada chamada.
 */
export async function createShareableStoreUrl(
  supplierSlug: string,
  queryParams?: Record<string, string>,
  preferredHost?: string | null
): Promise<string> {
  const key = shareCacheKey(supplierSlug, queryParams, preferredHost);
  const cached = readShareCache()[key];
  if (cached && Date.now() - cached.t <= SHARE_CACHE_TTL_MS) return cached.url;

  const pending = inFlightShareLinks.get(key);
  if (pending) return pending;

  const request = createShareableStoreUrlUncached(supplierSlug, queryParams, preferredHost)
    .then((url) => {
      // Só guarda links curtos de verdade (o fallback direto não é gravado, para tentar o curto da próxima vez).
      if (/\/orcamento\/|\/[a-z0-9]{6}$/i.test(url)) writeShareCache(key, url);
      return url;
    })
    .finally(() => { inFlightShareLinks.delete(key); });
  inFlightShareLinks.set(key, request);
  return request;
}

async function createShareableStoreUrlUncached(
  supplierSlug: string,
  queryParams?: Record<string, string>,
  preferredHost?: string | null
): Promise<string> {
  try {
    const response = await fetch(OG_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        supplier_slug: supplierSlug,
        params: queryParams || {},
      }),
    });

    if (!response.ok) throw new Error('Failed to create short link');

    const { code, share_host } = await response.json();
    if (!code) throw new Error('Invalid short link response');

    const host = normalizeHost(preferredHost) || normalizeHost(share_host) || DEFAULT_SHARE_HOST;
    return buildShortCodeUrl(code, host, supplierSlug);
  } catch (error) {
    console.error('Error creating short link, falling back to direct URL:', error);
    return buildDirectStoreUrl(supplierSlug, queryParams, preferredHost || DEFAULT_SHARE_HOST);
  }
}

/**
 * Builds a synchronous share URL (for QR codes, previews, etc.).
 */
export function buildShareableStoreUrl(
  supplierSlug: string,
  queryParams?: Record<string, string>,
  preferredHost?: string | null
): string {
  return buildDirectStoreUrl(supplierSlug, queryParams, preferredHost || DEFAULT_SHARE_HOST);
}

/**
 * Builds a short code URL under the branded short-link host.
 * New format: /orcamento/{slug}/{code} (slug visible for branding/trust).
 * Falls back to /{code} when slug is missing (legacy compatibility).
 */
export function buildShortCodeUrl(code: string, preferredHost?: string | null, supplierSlug?: string | null): string {
  const host = normalizeHost(preferredHost) || DEFAULT_SHARE_HOST;
  const path = supplierSlug ? `/orcamento/${supplierSlug}/${code}` : `/${code}`;
  return buildHostUrl(host, path);
}

/**
 * Builds the direct store URL.
 * On the generic host, /loja/:slug is handled by the Worker + edge function.
 */
export function buildDirectStoreUrl(
  supplierSlug: string,
  queryParams?: Record<string, string>,
  preferredHost?: string | null
): string {
  const customHost = normalizeHost(preferredHost);
  const params = new URLSearchParams();

  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }

  const qs = params.toString();

  if (customHost && customHost !== DEFAULT_SHARE_HOST) {
    // Keep the supplier slug in custom-domain links. Social crawlers do not
    // execute the browser-side custom-domain resolver, so the explicit route
    // lets the edge layer return the supplier's own Open Graph metadata.
    return buildHostUrl(customHost, `/loja/${supplierSlug}${qs ? `?${qs}` : ''}`);
  }

  return buildHostUrl(DEFAULT_SHARE_HOST, `/loja/${supplierSlug}${qs ? `?${qs}` : ''}`);
}
